import React, { FunctionComponent, ReactNode } from 'react';
import { Trans } from 'react-i18next';

import { OwnerType } from '../../../types/__generated__';
import PAGE_TYPES from '../../constants/pageTypes';
import { usePageMeta } from '../../hooks';
import { useProduct } from '../../hooks/page/product/useProduct';
import { useCharity } from '../../hooks/page/useCharity';
import useBasePageQuery from '../../hooks/useBasePageQuery';
import { filterByTypename } from '../../utils/graphql';
import { AboveTheFoldCtaPageOwnerAvatar } from '../AboveTheFoldCtaPageOwnerAvatar/AboveTheFoldCtaPageOwnerAvatar';
import styles from './AboveTheFoldCtaPageOwnerStory.scss';
import classNames from 'classnames';

const AboveTheFoldCtaPageOwnerStory: FunctionComponent = () => {
  const { data: basePageData } = useBasePageQuery();
  const { name, url } = useOwner();

  const { summary } = basePageData?.page ?? {};
  const charity = basePageData?.page?.relationships?.beneficiaries?.nodes
    ? filterByTypename(
        basePageData?.page?.relationships?.beneficiaries?.nodes,
        'Charity',
      )
    : null;
  const charityName = charity?.[0]?.name;
  const charityUrl = charity?.[0]?.profilePageUrl;

  return (
    <div className={styles.container}>
      <div className={styles.avatarCharityName}>
        <AboveTheFoldCtaPageOwnerAvatar />
        <div>
          {name && (
            <Owner as={url ? 'a' : 'span'} href={url}>
              <Trans i18nKey="fundraising.organisedBy">
                Fundraiser organised by {{ name: name }}
              </Trans>
            </Owner>
          )}
          {charityName && (
            <p className="jg-space-mbn" style={{ fontWeight: 400 }}>
              for <a href={charityUrl}>{charityName}</a>
            </p>
          )}
        </div>
      </div>
      {summary && (
        <p
          data-qa="page-owner-summary"
          className={classNames(
            'jg-text--light',
            'jg-pages-teams-charitysummary',
            'jg-space-mbn',
            'jg-space-ptxs',
            'jg-h5',
            'jg-space-plxs',
            'jg-space-prxs',
            styles.pageOwnerSummary,
          )}
        >
          "{capitalizeFirstLetter(summary)}"
        </p>
      )}
    </div>
  );
};

export default AboveTheFoldCtaPageOwnerStory;

const capitalizeFirstLetter = (input: string) =>
  `${input[0].toUpperCase()}${input.slice(1)}`;

interface OwnerProps {
  children: ReactNode;
  as: 'span' | 'a';
  href?: string;
}

function Owner({ as, children, ...props }: OwnerProps) {
  const Component = as;

  return (
    <Component {...props} className="jg-h6 jg-space-mbn jg-hero__owner-title">
      {children}
    </Component>
  );
}

function useOwner() {
  const { data: basePageData } = useBasePageQuery();
  const { pageType } = usePageMeta();
  const { name: charityName, profilePageUrl } = useCharity();
  const { ownerType } = useProduct();

  switch (true) {
    case pageType === PAGE_TYPES.FUNDRAISING ||
      (pageType === PAGE_TYPES.ONE_PAGE && ownerType === OwnerType.Individual):
      return {
        name: basePageData?.page?.owner?.name,
      };
    case pageType === PAGE_TYPES.CAMPAIGN ||
      (pageType === PAGE_TYPES.ONE_PAGE &&
        ownerType === OwnerType.Organisation):
      return {
        name: charityName,
        url: profilePageUrl,
      };
    default:
      return {};
  }
}
